import { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { Artifact } from '@/types';

interface ChartData {
  type: 'line' | 'bar' | 'pie';
  data: any[];
  xKey?: string;
  yKey?: string;
  title?: string;
  colors?: string[];
}

interface ChartRendererProps {
  artifact: Artifact;
}

export function ChartRenderer({ artifact }: ChartRendererProps) {
  const chartData = useMemo(() => {
    try {
      const data = typeof artifact.data === 'string' 
        ? JSON.parse(artifact.data) 
        : artifact.data;
      
      // Validate chart data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid chart data format');
      }
      
      return data as ChartData;
    } catch (error) {
      console.error('Error parsing chart data:', error);
      return null;
    }
  }, [artifact.data]);

  if (!chartData) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Unable to parse chart data. Expected JSON format with type, data, and configuration.
        </AlertDescription>
      </Alert>
    );
  }

  const { type, data, xKey = 'name', yKey = 'value', colors = [] } = chartData;

  // Default color palette
  const defaultColors = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00',
    '#0088fe', '#00c49f', '#ffbb28', '#ff8042', '#8dd1e1'
  ];

  const chartColors = colors.length > 0 ? colors : defaultColors;

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey={xKey} 
                className="text-muted-foreground"
                fontSize={12}
              />
              <YAxis 
                className="text-muted-foreground"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={yKey} 
                stroke={chartColors[0]} 
                strokeWidth={2}
                dot={{ fill: chartColors[0], strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey={xKey} 
                className="text-muted-foreground"
                fontSize={12}
              />
              <YAxis 
                className="text-muted-foreground"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Legend />
              <Bar 
                dataKey={yKey} 
                fill={chartColors[0]}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey={yKey}
              >
                {data.map((_, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={chartColors[index % chartColors.length]} 
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Unsupported chart type: {type}. Supported types: line, bar, pie.
            </AlertDescription>
          </Alert>
        );
    }
  };

  return (
    <Card className="border-0 shadow-none">
      <CardContent className="p-0">
        {chartData.title && (
          <h4 className="text-lg font-semibold mb-4 text-center">
            {chartData.title}
          </h4>
        )}
        {renderChart()}
      </CardContent>
    </Card>
  );
}